import './index.css'
import debounce from 'lodash/debounce'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'
import type { EmailCampaign } from 'types/campaign'
import type { Group, GroupId } from 'types/group'
import type { State } from 'types/redux'
import type { User } from 'types/user'
import {
  campaignDetailsSaveIfDirty,
  campaignDetailsUpdate,
} from '@disco/state-management/actions/campaign-details-actions'
import { loadEmailGroups } from '@disco/state-management/actions/EmailGroupActions'
import { CUSTOM_INBOX } from '@disco/constants/GroupTypes'
import { getCampaign } from '@disco/state-management/selectors/campaigns'
import {
  getCampaignSenderWithDefaults,
  getTemplateData,
  resolveCampaignBusinessAddress,
} from '@disco/utils/emailCampaigns'
import BusinessAddressField from 'components/common/business-address-field'
import GroupModal from 'components/common/custom-libraries/edit-form'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import { Spinner } from 'components/ui'
import AdminTool from 'components/ui/admin-tool'
import Button from 'components/ui/button'
import Label from 'components/ui/label'
import PageContainer from 'components/ui/page-container'
import TextField from 'components/ui/text-field'
import CampaignHeader from 'pages/campaigns/campaign-header'
import CampaignNav, { EMAIL_DETAILS_TAB } from 'pages/campaigns/campaign-nav'
import EditScheduledEmailModal from 'pages/campaigns/edit-scheduled-email-modal'
import DomainValidation from './domain-validation'
import InboxSelect from './inbox-select'
import type { RootState } from 'store/configureStore'
function mapStateToProps(state: RootState, ownProps: any) {
  return {
    user: state.user,
    emailGroups: state.emailGroups,
    campaign: getCampaign(state, {
      campaignId: ownProps.params.campaignId,
    }),
    modal: state.emailCampaigns.newCampaignModal,
  }
}

const mapDispatchToProps = {
  campaignDetailsUpdate,
  campaignDetailsSaveIfDirty,
  loadEmailGroups,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

interface AdditionalProps {
  navigate: NavigateFunction
  params: {
    campaignId: string
  }
}

type Props = PropsFromRedux & AdditionalProps

export class CampaignDetailsPage extends React.Component<
  Props,
  {
    showNewInboxModal: boolean
    addressResetKey: number
  }
> {
  state = {
    showNewInboxModal: false,
    addressResetKey: 0,
  }

  componentDidMount() {
    this.props.loadEmailGroups()
  }

  componentDidUpdate(prevProps: Props) {
    // the route swaps campaign without remounting; buffered edits must not follow
    if (prevProps.params.campaignId !== this.props.params.campaignId) {
      // this runs before campaign-master, which saves the campaign being left
      this._flushPending()
    }

    // clearing falls back to the account default; re-seed so the box shows what sends
    if (
      prevProps.campaign?.business_address_mjml &&
      !this.props.campaign?.business_address_mjml
    ) {
      this.setState(state => ({ addressResetKey: state.addressResetKey + 1 }))
    }
  }

  componentWillUnmount() {
    this._saveOnExit()
  }

  _flushPending = () => {
    this._saveFields.flush()
    this._saveBusinessAddress.flush()
  }

  // autosave waits up to 60s, so leaving would otherwise drop the edit
  _saveOnExit = () => {
    // the flush marks the campaign dirty, which is what saveIfDirty reads
    this._flushPending()
    this.props.campaignDetailsSaveIfDirty(
      parseInt(this.props.params.campaignId),
    )
  }

  _handleInboxChange = (value: GroupId | null | undefined) => {
    const campaignId = this.props.campaign.id
    this.props.campaignDetailsUpdate(campaignId, {
      inbox: value,
    })
  }
  _handleNextStep = async () => {
    const { campaign } = this.props
    let nextStep

    switch (campaign.type) {
      case 'campaign':
        nextStep = `/campaigns/${campaign.id}/template`
        break

      case 'briefing':
        nextStep = `/campaigns/${campaign.id}/editor`
        break
    }

    if (campaign.justDuplicated) {
      nextStep = `/campaigns/${campaign.id}/editor` // override
    }

    this._flushPending()
    this.props.campaignDetailsSaveIfDirty(campaign.id)
    this.props.navigate(nextStep)
  }
  _handleCreateInbox = () => {
    this.setState({
      showNewInboxModal: true,
    })
  }
  _onCloseInboxModal = (name: string, group: Group) => {
    if (group) {
      const campaignId = this.props.campaign.id
      this.props.campaignDetailsUpdate(campaignId, {
        inbox: group.id,
      })
    }

    this.setState({
      showNewInboxModal: false,
    })
  }
  // props can point at another campaign by the time a debounced save fires
  _pendingCampaign: EmailCampaign | null = null
  _pendingFields: Partial<EmailCampaign> = {}
  _pendingExtraFields: { subject?: string } = {}
  _handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value.trim()

    switch (evt.target.name) {
      case 'campaign-subject':
        this._pendingExtraFields.subject = value
        break

      case 'campaign-name':
        this._pendingFields.name = value
        break

      case 'sender-email':
        this._pendingFields.sender_email = value
        break

      case 'sender-name':
        this._pendingFields.sender_name = value
        break
    }

    this._pendingCampaign = this.props.campaign
    this._saveFields()
  }
  // one dispatch per typing pause, not one per keystroke
  _saveFields = debounce(() => {
    const campaign = this._pendingCampaign
    const basicFields = this._pendingFields
    const extraFields = this._pendingExtraFields
    this._pendingFields = {}
    this._pendingExtraFields = {}
    if (!campaign) return
    if (!Object.keys(basicFields).length && !Object.keys(extraFields).length) {
      return
    }
    this.props.campaignDetailsUpdate(campaign.id, basicFields, extraFields)
  }, 300)
  _handleBusinessAddressChange = (val, _valHtml, valMjml, valText) => {
    const isEmpty = !(valText || '').trim()
    this._pendingCampaign = this.props.campaign
    // null is the one representation of unset; '' would be a second one to interpret
    this._saveBusinessAddress(isEmpty ? null : val, isEmpty ? null : valMjml)
  }
  // editor fires onChange per keystroke and on selection change
  _saveBusinessAddress = debounce(
    (business_address: Record<string, any> | null, mjml: string | null) => {
      const campaign = this._pendingCampaign
      if (!campaign) return
      // UX guard only; the editor is read-only for non-admins
      if (
        !this.props.user.permissions?.includes('can_change_business_settings')
      ) {
        return
      }

      const current = resolveCampaignBusinessAddress(
        campaign,
        this.props.user.business,
      )
      // cursor moves reach onChange with unchanged content
      if ((current.business_address_mjml || '') === (mjml || '')) return
      this.props.campaignDetailsUpdate(campaign.id, {
        business_address,
        business_address_mjml: mjml,
      })
    },
    300,
  )

  _prefillSender(): {
    sender_name: string
    sender_email: string
  } {
    return getCampaignSenderWithDefaults(
      this.props.campaign,
      this.props.user,
      this.props.emailGroups.emailGroups,
    )
  }

  _renderPage(children: React.ReactNode) {
    const pageTitle = 'Email details | DISCO'
    return (
      <div className="Page_Content">
        <CampaignHeader
          campaignId={this.props.params.campaignId}
          onNextClick={this._handleNextStep}
        />
        <DocumentHeadTags title={pageTitle} />
        {children}
      </div>
    )
  }

  render() {
    const { campaign } = this.props
    let inboxSelect = null

    if (!this.props.emailGroups.isFetched) {
      return this._renderPage(<Spinner />)
    }

    if (campaign.type === 'briefing') {
      let inboxCreateModal = null

      if (this.state.showNewInboxModal) {
        inboxCreateModal = (
          <GroupModal
            user={this.props.user}
            groupType={CUSTOM_INBOX}
            onSubmit={this._onCloseInboxModal}
            onClose={this._onCloseInboxModal}
          />
        )
      }

      inboxSelect = (
        <div className="CampaignDetails_InboxCont">
          <div className="CampaignDetails_InboxLabel">
            Select response inbox (optional)
          </div>
          <div>
            <span className="CampaignDetails_InboxInput">
              <InboxSelect
                value={campaign.inbox}
                onChange={this._handleInboxChange}
              />
            </span>
            <span className="CampaignDetails_OrSplitter">or</span>
            <Button
              caption="Create new inbox"
              onClick={this._handleCreateInbox}
            />
          </div>
          {inboxCreateModal}
        </div>
      )
    }

    const template_data = getTemplateData(campaign)

    const { sender_name, sender_email } = this._prefillSender()

    const { business_address, business_address_mjml } =
      resolveCampaignBusinessAddress(campaign, this.props.user.business)

    return this._renderPage(
      <PageContainer>
        <CampaignNav
          campaignId={this.props.params.campaignId}
          activeStep={EMAIL_DETAILS_TAB}
        />
        <PageContainer.Section>
          <Row columnsSize={[6, 6]}>
            <div>
              <TextField
                label="Email name"
                name="campaign-name"
                defaultValue={campaign.name}
                onChange={this._handleChange}
              />
            </div>
            <div>
              <TextField
                label="Email subject"
                name="campaign-subject"
                defaultValue={template_data.subject}
                onChange={this._handleChange}
              />
            </div>
          </Row>
          <Row columnsSize={[6, 6]}>
            <div>
              <TextField
                label="Sender from address"
                name="sender-email"
                defaultValue={sender_email}
                onChange={this._handleChange}
              />
            </div>
            <div>
              <TextField
                label="Sender display name"
                name="sender-name"
                defaultValue={sender_name}
                onChange={this._handleChange}
              />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <label className="Form_Label">
              <Label
                caption={`Business address${
                  !campaign.business_address_mjml && business_address_mjml
                    ? ' (account default)'
                    : ''
                }`}
                validationError={
                  business_address_mjml
                    ? null
                    : 'Please enter a business address'
                }
              />
              {/* seeded once on mount, so re-seed when the campaign is replaced */}
              <BusinessAddressField
                key={`${campaign.id}:${campaign.reloadCount ?? 0}:${
                  this.state.addressResetKey
                }`}
                invalid={!business_address_mjml}
                initialContent={business_address}
                onChange={this._handleBusinessAddressChange}
              />
            </label>
          </Row>

          <AdminTool
            renderFn={() => <DomainValidation email={sender_email} />}
          />

          {inboxSelect && (
            <div className="CampaignDetails_OptionalRow">
              <div className="CampaignDetails_InputWrap">{inboxSelect}</div>
            </div>
          )}
        </PageContainer.Section>
        <EditScheduledEmailModal campaignId={this.props.params.campaignId} />
      </PageContainer>,
    )
  }
}

const ConnectedCampaignDetailsPage = connector(CampaignDetailsPage)

function CampaignDetailsPageWithRouter(props: any) {
  const navigate = useNavigate()
  const params = useParams<{ campaignId: string }>()
  return (
    <ConnectedCampaignDetailsPage
      {...props}
      navigate={navigate}
      params={params}
    />
  )
}

export default CampaignDetailsPageWithRouter
